import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { get_customer_detail } from "../../utils/api";
import {
  Box,
  Card,
  Flex,
  Heading,
  SimpleGrid,
  Text,
  VStack,
  useDisclosure,
  Badge,
} from "@chakra-ui/react";
import CustomButton from "../../components/ButtonComp";
import { BiArrowBack } from "react-icons/bi";
import DisplayData from "../../components/DisplayData";
import Loader from "../../components/Loader";
import ImageModal from "../../components/ImageModal";
import CustomDataCard from "../../components/CustomDataCard";
import { FiMail, FiPhone, FiUser } from "react-icons/fi";

const CustomerDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const { onOpen, onClose, isOpen } = useDisclosure();
  const [url, setUrl] = useState("");

  useEffect(() => {
    get_customer_detail(location.state?.id, setLoading).then((el) => {
      if (el.success) {
        setData(el.data[0]);
      } else {
        console.error("Failed to fetch customer details");
      }
    });
  }, []);
  console.log(
    location?.state?.branches?.map((customer) => customer?.tradename)
  );

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case "1":
        return "green";
      case "0":
        return "red";
      default:
        return "gray";
    }
  };

  // Get status text
  const getStatusText = (status) => {
    switch (status) {
      case "1":
        return "Active";
      case "0":
        return "Inactive";
      default:
        return "Unknown";
    }
  };

  // Convert customer data to card format
  const createCustomerCardData = (customer) => [
    { key: "User ID", value: customer.userid },
    {
      key: "Mobile",
      value: customer.mobile,
      icon: FiPhone,
      iconColor: "green.500",
    },
    {
      key: "Email",
      value: customer.email,
      icon: FiMail,
      iconColor: "blue.500",
    },
    { key: "GST Number", value: customer.gst, valueWeight: "semibold" },
    { key: "PAN Number", value: customer.pan, valueWeight: "medium" },
    {
      key: "Trade Name",
      value: customer.tradename,
      // valueSize: "md",
      // valueWeight: "semibold",
    },
    { key: "Address", value: customer.address, maxLines: 3 },
    { key: "District", value: customer.district },
    { key: "State", value: customer.state },
    { key: "Pincode", value: customer.pincode, badge: "ZIP" },
    {
      key: "Branch",
      value: customer.branch,
      badge: customer.branch ? "Active" : "Inactive",
      badgeColorScheme: customer.branch ? "green" : "red",
    },
  ];
  return (
    <>
      <Card
        display={"flex"}
        flexDirection={"row"}
        alignItems={"center"}
        justify={"space-between"}
        paddingInline={5}
        paddingBlock={2}
      >
        <Heading fontSize={"xl"}>Customer Details</Heading>
        <CustomButton
          icon={<BiArrowBack color="#fff" />}
          title={"Back"}
          height={"30px"}
          b_color={"#000"}
          onClick={() => navigate(-1)}
        />
      </Card>

      {loading ? (
        <Card p={5} justify={"center"} alignItems={"center"}>
          <Loader />
        </Card>
      ) : (
        <VStack spacing={4} align="stretch">
          {/* Basic Information Card */}
          <Card paddingInline={5} paddingBlock={4}>
            <Heading fontSize={"lg"} mb={4} color="blue.600">
              Basic Information
            </Heading>
            <SimpleGrid columns={[1, 2, 3]} spacing={4}>
              <Box>
                <Flex align="center" gap={2} mb={2}>
                  <DisplayData
                    fs={"18px"}
                    v_color={"#007bc9"}
                    fw={"bold"}
                    title={"Legal Name"}
                    value={data?.legalname || "N/A"}
                  />
                </Flex>
                <DisplayData
                  title={"Trade Name"}
                  value={data?.tradename || "N/A"}
                />
              </Box>

              <Box>
                <DisplayData
                  title={"User ID"}
                  v_color={"#f37022"}
                  fw={"bold"}
                  value={data?.userid || "N/A"}
                />
                <DisplayData
                  title={"Customer Type"}
                  value={data?.type || "N/A"}
                />
              </Box>

              <Box>
                <Flex align="center" gap={2}>
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">
                    Status:
                  </Text>
                  <Badge colorScheme={getStatusColor(data?.status)}>
                    {getStatusText(data?.status)}
                  </Badge>
                </Flex>
                <DisplayData title={"Branch"} value={data?.branch || "N/A"} />
              </Box>
            </SimpleGrid>
          </Card>

          {/* Contact Information Card */}
          <Card paddingInline={5} paddingBlock={4}>
            <Heading fontSize={"lg"} mb={4} color="green.600">
              Contact Information
            </Heading>
            <SimpleGrid columns={[1, 2]} spacing={4}>
              <Box>
                <DisplayData
                  title={"Mobile Number"}
                  value={data?.mobile || "N/A"}
                />
                <DisplayData title={"Email"} value={data?.email || "N/A"} />
                {data?.email2 && (
                  <DisplayData title={"Secondary Email"} value={data.email2} />
                )}
              </Box>
              <Box>
                <DisplayData title={"Address"} value={data?.address || "N/A"} />
                <DisplayData title={"State"} value={data?.state || "N/A"} />
                <DisplayData
                  title={"District"}
                  value={data?.district || "N/A"}
                />
                <DisplayData title={"Pincode"} value={data?.pincode || "N/A"} />
              </Box>
            </SimpleGrid>
          </Card>

          {/* Business Information Card */}
          <Card paddingInline={5} paddingBlock={4}>
            <Heading fontSize={"lg"} mb={4} color="purple.600">
              Business Information
            </Heading>
            <SimpleGrid columns={[1, 2, 3]} spacing={4}>
              <DisplayData title={"GST Number"} value={data?.gst || "N/A"} />
              <DisplayData title={"PAN Number"} value={data?.pan || "N/A"} />
              <DisplayData
                title={"Security Deposit"}
                value={
                  data?.securitydeposit ? `₹${data.securitydeposit}` : "₹0"
                }
              />
            </SimpleGrid>
          </Card>

          {/* Account Information Card */}
          <Card paddingInline={5} paddingBlock={4}>
            <Heading fontSize={"lg"} mb={4} color="orange.600">
              Account Information
            </Heading>
            <SimpleGrid columns={[1, 2]} spacing={4}>
              <DisplayData
                title={"Account Created"}
                value={formatDate(data?.created_at)}
              />
              <DisplayData
                title={"Parent ID"}
                value={data?.parentid || "N/A"}
              />
              <DisplayData
                title={"Referred By"}
                value={data?.referredby !== "0" ? data?.referredby : "Direct"}
              />
              {data?.referredcode && (
                <DisplayData
                  title={"Referral Code"}
                  value={data.referredcode}
                />
              )}
            </SimpleGrid>
          </Card>

          {/* Bank Details Card */}
          {data?.bank && data.bank.length > 0 && (
            <Card paddingInline={5} paddingBlock={4}>
              <Heading fontSize={"lg"} mb={4} color="teal.600">
                Bank Details
              </Heading>
              {data.bank.map((bankInfo, index) => (
                <Card key={index} bg="gray.50" p={3} mb={2}>
                  <SimpleGrid columns={[1, 2]} spacing={4}>
                    <DisplayData
                      title={"Bank Name"}
                      value={bankInfo.bankname || "N/A"}
                    />
                    <DisplayData
                      title={"Account Number"}
                      value={bankInfo.account_no || "N/A"}
                    />
                    <DisplayData
                      title={"Branch Name"}
                      value={bankInfo.branch_name || "N/A"}
                    />
                    <DisplayData
                      title={"IFSC Code"}
                      value={bankInfo.ifsc_code || "N/A"}
                    />
                  </SimpleGrid>
                </Card>
              ))}
            </Card>
          )}

          {/* Credit Information Card */}
          {data?.credit && data.credit.length > 0 && (
            <Card paddingInline={5} paddingBlock={4}>
              <Heading fontSize={"lg"} mb={4} color="red.600">
                Credit Information
              </Heading>
              {data.credit.map((creditInfo, index) => (
                <Card key={index} bg="red.50" p={3} mb={2}>
                  <Text>Credit Info #{index + 1}</Text>
                  {/* Add specific credit fields based on your API structure */}
                </Card>
              ))}
            </Card>
          )}

          {/* Personal Information Card (if available) */}
          {data?.personalinfo && data.personalinfo.length > 0 && (
            <Card paddingInline={5} paddingBlock={4}>
              <Heading fontSize={"lg"} mb={4} color="pink.600">
                Personal Information
              </Heading>
              {data.personalinfo.map((personalInfo, index) => (
                <Card key={index} bg="pink.50" p={3} mb={2}>
                  <Text>Personal Info #{index + 1}</Text>
                  {/* Add specific personal info fields based on your API structure */}
                </Card>
              ))}
            </Card>
          )}

          {/* Document Proofs Card (if available) */}
          {(data?.address_proof || data?.id_proof) && (
            <Card paddingInline={5} paddingBlock={4}>
              <Heading fontSize={"lg"} mb={4} color="cyan.600">
                Document Information
              </Heading>
              <SimpleGrid columns={[1, 2]} spacing={4}>
                {data?.addr_proof_type && (
                  <DisplayData
                    title={"Address Proof Type"}
                    value={data.addr_proof_type}
                  />
                )}
                {data?.address_proof && (
                  <Box>
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      color="gray.600"
                      mb={1}
                    >
                      Address Proof:
                    </Text>
                    <CustomButton
                      title={"View Document"}
                      height={"30px"}
                      onClick={() => {
                        setUrl(data.address_proof);
                        onOpen();
                      }}
                    />
                  </Box>
                )}
                {data?.id_proof && (
                  <Box>
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      color="gray.600"
                      mb={1}
                    >
                      ID Proof:
                    </Text>
                    <CustomButton
                      title={"View Document"}
                      height={"30px"}
                      onClick={() => {
                        setUrl(data.id_proof);
                        onOpen();
                      }}
                    />
                  </Box>
                )}
              </SimpleGrid>
            </Card>
          )}
          {/* <Heading fontSize={"lg"} color="blue.600">
            Branches
          </Heading>
          <SimpleGrid columns={[1, 1, 2, 3]} spacing={6}>
            {location?.state?.branches?.map((customer) => (
              <CustomDataCard
                key={customer.id}
                title={customer.tradename || "Customer Profile"}
                titleColor="blue.800"
                // titleSize="lg"
                // titleWeight="bold"
                subtitle={`ID: ${customer.id} • Branch: ${
                  customer.branch || "Not assigned"
                }`}
                subtitleColor="blue.600"
                subtitleSize="sm"
                data={createCustomerCardData(customer).slice(0, 6)} // Show first 6 items
                keyWidth="100px"
                keyColor="gray.700"
                keyWeight="medium"
                valueColor="gray.600"
                valueAlign="left"
                // headerIcon={FiUser}
                hoverEffect={true}
                shadow="lg"
              />
            ))}
          </SimpleGrid> */}
        </VStack>
      )}

      <ImageModal url={url} onClose={onClose} isOpen={isOpen} />
    </>
  );
};

export default CustomerDetail;
